import { DEFAULT_REGION } from '@/utils/constants'
import { Data } from '@olxbr/clodoview-react-renderer'
import { Env } from '@olxbr/pos-react-components'
import Script from 'next/script'
import type { HashedUserData } from '@/utils/hash'

interface LurkerPros {
  enabled: boolean
  data?: Data
  webVitalsEnabled: boolean
  publicAccountId?: string
  businessUnit?: string
  hashedUserData?: HashedUserData
}

const loadWebVitals = () => (
  <>
    <Script
      id="web-vitals-plugin"
      type="text/javascript"
      src="https://static.olx.com.br/web-vitals/web-vitals.js"
      defer
    />
    <Script
      id="web-vitals-tracker-plugin"
      type="text/javascript"
      src="https://static.olx.com.br/web-vitals/web-vitals-tracker.js"
      defer
    />
  </>
)

const associate = (key: string, value: unknown) => {
  return value ? `${key}: '${value}',` : ``
}

const associateRegion = (region: unknown) => {
  return region && region != DEFAULT_REGION ? `region_id: '${region}',` : ``
}

const getVertical = (pointOfSale?: string) => {
  const autosPos = process.env.NEXT_PUBLIC_AUTOS_POINTS_OF_SALE?.split(`,`) || [
    `carros`,
    `motos`,
    `caminhoes`,
    `onibus`,
    `barcos`,
    `veiculos-autonomo`,
    `veiculos-empresa`,
  ]
  const rePos = process.env.NEXT_PUBLIC_RE_POINTS_OF_SALE?.split(`,`) || [
    `imoveis`,
  ]
  const goodsPos = process.env.NEXT_PUBLIC_GOODS_POINTS_OF_SALE?.split(`,`) || [
    `auto-pecas`,
    `marketplace`,
  ]

  if (pointOfSale && autosPos.includes(pointOfSale)) return `autos`
  if (pointOfSale && rePos.includes(pointOfSale)) return `real-estate`
  if (pointOfSale && goodsPos.includes(pointOfSale)) return `goods`
  return pointOfSale
}

const associateVertical = (posType: string, pointOfSale?: string) => {
  return posType === `plano-profissional`
    ? `vertical: '${getVertical(pointOfSale)}',`
    : ``
}

const associateAnswerToProfileQuestion = () => {
  return `
    ...(function() {
      try {
        var _apq = window.localStorage && window.localStorage.getItem('pre-pos-question-ab');
        return _apq ? { answer_to_profile_question: _apq } : {};
      } catch(e) { return {}; }
    }()),
  `
}

const loadLurker = (props: LurkerPros) => {
  const env: Env = props.data?.env as Env
  const pointOfSale = (props.data?.parentPos as string) || env?.type
  const posType = props.data?.adId ? `payg` : `plano-profissional`
  return (
    <>
      <Script
        id="lurker-plugin"
        type="text/javascript"
        src="https://static.olx.com.br/olx/js/lurker.min.js"
      />
      <Script
        id="lurker-data-layer"
        dangerouslySetInnerHTML={{
          __html: `
          initLurkerDataLayer = function() {
            if (window) {
              window.dataLayer = window.dataLayer || []
              window.dataLayer.push({
                event: 'page_view',
                brand: '${props.businessUnit}',
                checkout_offline: false,
                checkout_type: '${posType}',
                content_group: '${posType}',
                point_of_sale: '${pointOfSale}',
                ${associateVertical(posType, env?.type)}
                ${associate(`user_id`, props.publicAccountId)}
                ${associate(`user_phone`, props.hashedUserData?.userPhone)}
                ${associate(`user_email`, props.hashedUserData?.userEmail)}
                ${associate(`user_zip_code`, props.hashedUserData?.userZipcode)}
                user_logged_in: ${Boolean(props.publicAccountId)},
                page: {
                  pageType: 'point_of_sales',
                  detail: {
                    ${associate(`referral`, env?.referral)}
                    ${associate(`funnel_id`, env?.funnelId)}
                    ${associate(`point_of_sale`, pointOfSale)}
                    ${associate(`point_of_sale_view`, env?.clodoviewKey)}
                    ${associate(`ad_id`, props.data?.adId)}
                    ${associate(`list_id`, props.data?.listId)}
                    ${associate(`category_id`, props.data?.categoryId)}
                    ${associate(`zipcode`, props.data?.zipcode)}
                    ${associateRegion(props.data?.region)}
                    ${associateAnswerToProfileQuestion()}
                    pos_platform: true,
                    has_discount: ${props.data?.hasDiscount},
                  },
                  site:{
                    ${associate(`source`, env?.platform)}
                  }
                }
              })
            }
          }
          initLurkerDataLayer()
        `,
        }}
      />
      {props.webVitalsEnabled && loadWebVitals()}
    </>
  )
}

export const Lurker = (props: LurkerPros) => (
  <>{props.enabled && (props.data?.env as Env) && loadLurker(props)}</>
)

export default Lurker
